import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Gamepad2, Plus, Edit, Users, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useMecanicas, useDeleteMecanica } from "@/hooks/useMecanicas";
import { unifiedStorage } from "@/lib/unified-storage";
import { useAuth } from "@/hooks/useAuth";

export default function EventsMecanicas() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { eventProfile } = useAuth();
  const url = searchParams.get('url');
  const eventId = searchParams.get('eventId') || searchParams.get('eventoId');
  const isPromotor = eventProfile?.role === 'staff';
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const { data: mecanicas = [], isLoading } = useMecanicas();
  const deleteMutation = useDeleteMecanica();

  useEffect(() => {
    if (eventId) {
      unifiedStorage.setEventId(eventId);
    }
  }, [eventId]);

  const handleNovaMecanica = () => {
    navigate(`/mecanicas/nova${location.search}`);
  };

  const handleEditarMecanica = (id: string) => {
    navigate(`/mecanicas/${id}${location.search}`);
  };

  const handleDeletarMecanica = async (id: string) => {
    const mecanica = mecanicas.find(m => m.id === id);
    const participantCount = mecanica?.quantidadeParticipacao || 0;

    let confirmMessage = `Tem certeza que deseja deletar a mecânica "${mecanica?.nome}"?`;
    if (participantCount > 0) {
      confirmMessage += `\n\nAtenção: Esta mecânica possui ${participantCount} participante(s).`;
    }

    if (window.confirm(confirmMessage)) {
      setIsDeleting(id);
      deleteMutation.mutate(id, {
        onSuccess: () => {
          toast.success("Mecânica deletada com sucesso");
        },
        onError: (error: any) => {
          console.error('Delete error:', error);

          const message = error?.response?.data?.message || "Não foi possível deletar a mecânica";

          if (message.includes('active entries') || message.includes('Cannot delete queue with active entries')) {
            const participantCount = mecanica?.quantidadeParticipacao || 0;
            toast.error(`Esta mecânica possui ${participantCount} participante${participantCount !== 1 ? 's' : ''} na fila. Por favor, processe ou transfira todos os participantes antes de deletar a mecânica.`, {
              duration: 5000,
            });
          } else if (error?.response?.status === 409) {
            toast.error("Esta mecânica ainda possui dados vinculados. Verifique se há participantes ou outras dependências.", {
              duration: 5000,
            });
          } else {
            toast.error(message);
          }
        },
        onSettled: () => {
          setIsDeleting(null);
        },
      });
    }
  };

  const handleParticipantes = (id: string) => {
    navigate(`/mecanicas/participantes/${id}${location.search}`);
  };

  const getStatusColor = (status: string) => {
    return status === 'aberta' 
      ? 'bg-brand-green/20 text-status-ok' 
      : 'bg-brand-danger/20 text-status-danger';
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString('pt-BR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[var(--radius)] bg-brand-logo-2/10">
            <Gamepad2 className="text-brand-logo-2" size={20} />
          </div>
          <div className="flex-1">
            <h1 className="text-[16px] font-bold text-foreground">Mecânicas Interativas</h1>
            <p className="text-[13px] text-muted-foreground">
              Gerencie e configure mecânicas de engajamento
            </p>
            {url && (
              <p className="text-sm text-brand-logo-1 mt-1">
                Conectado a: {decodeURIComponent(url)}
              </p>
            )}
          </div>
        </div>
        <button onClick={handleNovaMecanica} className="btn-primary flex items-center gap-2 w-full sm:w-auto">
          <Plus size={20} />
          Cadastrar Mecânica
        </button>
      </div>

      {/* Mecânicas Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="animate-pulse flex items-center gap-4">
                  <div className="w-48 h-6 bg-muted rounded"></div>
                  <div className="w-24 h-6 bg-muted rounded"></div>
                  <div className="w-16 h-6 bg-muted rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : mecanicas.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-brand-logo-2/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gamepad2 className="text-brand-logo-2" size={40} />
            </div>
            <h3 className="text-[16px] font-semibold text-foreground mb-2">Nenhuma mecânica encontrada</h3>
            <p className="text-muted-foreground mb-6">
              Começe criando sua primeira mecânica interativa
            </p>
            <button onClick={handleNovaMecanica} className="btn-primary">
              Criar Primeira Mecânica
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Mecânica</th>
                    <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Participantes</th>
                    <th className="text-left px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Status</th>
                    <th className="text-right px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {mecanicas.map((mecanica) => (
                    <tr key={mecanica.id} className="hover:bg-[var(--tabela-linha-hover)] transition-colors">
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-brand-logo-2/10 rounded-ds-card">
                            <Gamepad2 className="text-brand-logo-2" size={20} />
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{mecanica.nome}</p>
                            <p className="text-sm text-muted-foreground">
                              ID: {mecanica.id}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div>
                          <p className="text-foreground font-medium">
                            {formatNumber(mecanica.quantidadeParticipacao)}
                            {mecanica.maxParticipants && ` / ${formatNumber(mecanica.maxParticipants)}`}
                            {!mecanica.maxParticipants && ' / ∞'}
                          </p>
                          {mecanica.maxParticipants && (
                            <div className="mt-1">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full transition-all ${
                                    mecanica.quantidadeParticipacao / mecanica.maxParticipants >= 0.95 
                                      ? 'bg-brand-danger' 
                                      : mecanica.quantidadeParticipacao / mecanica.maxParticipants >= 0.8 
                                      ? 'bg-brand-attention' 
                                      : 'bg-brand-green'
                                  }`}
                                  style={{ width: `${Math.min(100, (mecanica.quantidadeParticipacao / mecanica.maxParticipants) * 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(mecanica.status)}`}>
                          {mecanica.status.charAt(0).toUpperCase() + mecanica.status.slice(1)}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleParticipantes(mecanica.id)}
                            className="p-2 hover:bg-brand-green/10 rounded-lg transition-colors group"
                            title="Ver participantes"
                          >
                            <Users size={20} className="text-muted-foreground group-hover:text-status-ok" />
                          </button>
                          {!isPromotor && (
                            <button
                              onClick={() => handleEditarMecanica(mecanica.id)}
                              className="p-2 hover:bg-brand-logo-1/10 rounded-lg transition-colors group"
                              title="Editar mecânica"
                            >
                              <Edit size={20} className="text-muted-foreground group-hover:text-brand-logo-1" />
                            </button>
                          )}
                          {!isPromotor && (
                            <button
                              onClick={() => handleDeletarMecanica(mecanica.id)}
                              disabled={isDeleting === mecanica.id}
                              className="p-2 hover:bg-brand-danger/10 rounded-lg transition-colors group disabled:opacity-50"
                              title="Deletar mecânica"
                            >
                              {isDeleting === mecanica.id ? (
                                <Loader2 size={20} className="text-status-danger animate-spin" />
                              ) : (
                                <Trash2 size={20} className="text-muted-foreground group-hover:text-status-danger" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="block sm:hidden divide-y divide-border">
              {mecanicas.map((mecanica) => (
                <div key={mecanica.id} className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-brand-logo-2/10 rounded-ds-card">
                      <Gamepad2 className="text-brand-logo-2" size={20} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{mecanica.nome}</p>
                      <p className="text-sm text-muted-foreground">ID: {mecanica.id}</p>
                    </div>
                    <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(mecanica.status)}`}>
                      {mecanica.status.charAt(0).toUpperCase() + mecanica.status.slice(1)}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-muted-foreground" />
                        <span className="text-sm text-foreground">
                          {formatNumber(mecanica.quantidadeParticipacao)}
                          {mecanica.maxParticipants && ` / ${formatNumber(mecanica.maxParticipants)}`}
                          {!mecanica.maxParticipants && ' / ∞'} participantes
                        </span>
                      </div>
                      {mecanica.maxParticipants && (
                        <div className="mt-2">
                          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all ${
                                mecanica.quantidadeParticipacao / mecanica.maxParticipants >= 0.95 
                                  ? 'bg-brand-danger' 
                                  : mecanica.quantidadeParticipacao / mecanica.maxParticipants >= 0.8 
                                  ? 'bg-brand-attention' 
                                  : 'bg-brand-green'
                              }`}
                              style={{ width: `${Math.min(100, (mecanica.quantidadeParticipacao / mecanica.maxParticipants) * 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleParticipantes(mecanica.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-brand-green/10 text-status-ok rounded-lg hover:bg-brand-green/20 transition-colors"
                    >
                      <Users size={16} />
                      <span className="text-sm font-medium">Participantes</span>
                    </button>
                    {!isPromotor && (
                      <button
                        onClick={() => handleEditarMecanica(mecanica.id)}
                        className="flex items-center justify-center gap-2 px-3 py-2 bg-brand-logo-1/10 text-brand-logo-1 rounded-lg hover:bg-brand-logo-1/20 transition-colors"
                      >
                        <Edit size={16} />
                        <span className="text-sm font-medium">Editar</span>
                      </button>
                    )}
                    {!isPromotor && (
                      <button
                        onClick={() => handleDeletarMecanica(mecanica.id)}
                        disabled={isDeleting === mecanica.id}
                        className="flex items-center justify-center gap-2 px-3 py-2 bg-brand-danger/10 text-status-danger rounded-lg hover:bg-brand-danger/20 transition-colors disabled:opacity-50"
                      >
                        {isDeleting === mecanica.id ? (
                          <Loader2 size={16} className="text-status-danger animate-spin" />
                        ) : (
                          <Trash2 size={16} />
                        )}
                        <span className="text-sm font-medium">Deletar</span>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}